import { useCallback, useEffect, useRef, useState } from 'react';
import type { TicketSummary } from '@nbh/shared';
import { api } from '../../lib/api';
import { cacheFirst } from '../../lib/cacheFirst';
import { cacheTicketList, getCachedTicketList } from '../../lib/db';
import { propertyTimeZone } from '../../config/property';
import { useRealtime } from '../../hooks/useRealtime';
import { type QueueView, cacheFilterFor, queryFor } from './queueView';

/**
 * The rows behind one view of the queue, cache first (Rule 4).
 *
 * A technician standing in a plant room with their jobs on screen is the point of the
 * whole application. The cache is keyed per view — see `cacheFilterFor` — and holds the
 * rows in the order the server sent them; re-sorting or re-filtering here would mean
 * reproducing server logic from guesswork, which is the same thing the
 * refetch-on-realtime below refuses to do.
 *
 * A view change clears the list before reading; a realtime refresh does not. The first
 * keeps a row from the previous tab from sitting under the new tab's heading while the
 * answer is in flight; the second keeps every ticket update from blanking the screen.
 */
export function useQueueList(view: QueueView) {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cancels the read in flight. A realtime event can start another before the first has
  // answered, and the loser must not set state after the winner.
  const cancelRef = useRef<(() => void) | null>(null);

  const load = useCallback(
    (reset: boolean) => {
      setError(null);
      if (reset) {
        setTickets([]);
        setLoading(true);
      }
      cancelRef.current?.();
      const cacheFilter = cacheFilterFor(view);
      cancelRef.current = cacheFirst<TicketSummary[]>({
        readCache: () => getCachedTicketList(cacheFilter),
        // Resolved at request time, so a "last 7 days" preset asks for today's week
        // while its cache row stays under the preset's own key.
        load: () => api.listTickets(queryFor(view, new Date(), propertyTimeZone)),
        writeCache: (rows) => cacheTicketList(cacheFilter, rows),
        onValue: (rows) => {
          setTickets(rows);
          setLoading(false);
        },
        onError: (caught) => {
          setError(caught.message);
          setLoading(false);
        },
      });
    },
    [view],
  );

  useEffect(() => {
    load(true);
    return () => cancelRef.current?.();
  }, [load]);

  // A new or changed ticket refetches rather than patching in place: the list is
  // sorted and filtered server-side, so guessing the new position would drift.
  const refresh = useCallback(() => load(false), [load]);
  useRealtime({ onTicketCreated: refresh, onTicketUpdated: refresh });

  return { tickets, loading, error };
}
